import React, { useState } from 'react';
import { formatTimeDisplay } from '../../../../../lib/utils/dateTimeFormat';
import BookingInvoice from '../bookingInvoice/BookingInvoice';
import EditBooking from '../editBooking/EditBooking';
import './BookingModal.css';

const BookingModal = ({ booking, isOpen, onClose, onEdit }) => {
  const [showInvoice, setShowInvoice] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  if (!isOpen || !booking) return null;

  const isGuestBooking = booking.guest_profile_id && !booking.user_id;
  const customerName = isGuestBooking 
    ? `${booking.guest_profiles?.first_name || ''} ${booking.guest_profiles?.last_name || ''}`
    : `${booking.profiles?.first_name || ''} ${booking.profiles?.last_name || ''}`;

  const customerEmail = isGuestBooking 
    ? booking.guest_profiles?.email 
    : booking.profiles?.email;

  const customerPhone = isGuestBooking 
    ? booking.guest_profiles?.phone_number 
    : booking.profiles?.phone_number;

  const handlePrintInvoice = () => {
    setShowInvoice(true);
    setTimeout(() => {
      window.print();
      setShowInvoice(false);
    }, 100);
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleEditSave = async (bookingId, updatedData) => {
    try {
      await onEdit(bookingId, updatedData);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  if (showInvoice) {
    return <BookingInvoice booking={booking} />;
  }

  if (showEditModal) {
    return (
      <EditBooking
        booking={booking}
        onClose={() => setShowEditModal(false)}
        onSave={handleEditSave}
      />
    );
  }

  return (
    <div className="booking-modal-overlay" onClick={onClose}>
      <div className="booking-modal" onClick={e => e.stopPropagation()}>
        <div className="booking-modal-header">
          <h2>Booking Details</h2>
          <div className="modal-actions">
            <button className="edit-btn" onClick={handleEditClick}>
              Edit Booking
            </button>
            <button className="print-invoice-btn" onClick={handlePrintInvoice}>
              Print Invoice
            </button>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
        </div>
        
        <div className="booking-modal-content">
          <div className="booking-section">
            <h3>Customer Information</h3>
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{customerName.trim() || 'N/A'}</span>
              {isGuestBooking && <span className="guest-badge">Guest</span>}
            </div>
            <div className="info-row">
              <span className="label">Email:</span>
              <span className="value">{customerEmail || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="label">Phone:</span>
              <span className="value">{customerPhone || 'N/A'}</span>
            </div>
          </div>

          <div className="booking-section">
            <h3>Service Details</h3>
            <div className="info-row">
              <span className="label">Service:</span>
              <span className="value">{booking.services?.name || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="label">Vehicle Type:</span>
              <span className="value">{booking.vehicle_types?.title || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="label">Date:</span>
              <span className="value">{booking.booking_date}</span>
            </div>
            <div className="info-row">
              <span className="label">Time:</span>
              <span className="value">{formatTimeDisplay(booking.booking_time)}</span>
            </div>
          </div>

          <div className="booking-section">
            <h3>Location Details</h3>
            <div className="info-row">
              <span className="label">Service Location:</span>
              <span className="value location-type">
                {booking.location_service === 'atHome' ? 'At Home Service' : 'In Shop Service'}
              </span>
            </div>
            {booking.location_service === 'atHome' && booking.address && (
              <div className="info-row">
                <span className="label">Address:</span>
                <span className="value address">{booking.address}</span>
              </div>
            )}
          </div>

          {booking.booking_addons?.length > 0 && (
            <div className="booking-section">
              <h3>Selected Add-ons</h3>
              <div className="addons-list">
                {booking.booking_addons.map((addon) => (
                  <div key={addon.service_addons.id} className="addon-item">
                    <span className="addon-name">{addon.service_addons.name}</span>
                    <span className="addon-price">${addon.service_addons.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="booking-section">
            <h3>Booking Status</h3>
            <div className="info-row">
              <span className="label">Status:</span>
              <span className={`status-badge status-${booking.status.toLowerCase()}`}>
                {booking.status}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Total Price:</span>
              <span className="value">${booking.total_price}</span>
            </div>
          </div>

          {booking.notes && (
            <div className="booking-section">
              <h3>Additional Notes</h3>
              <div className="notes-content">
                {booking.notes}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BookingModal;
